import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/queries/useUser';
import { StanCrop } from '@/components';
import { StanFileUpload, StanFileUploadRef } from '@/components/core-components/StanFileUpload/StanFileUpload';
import { uploadFile } from '@/lib/api/fileUpload';
import { useUpdateProfileImage } from '@/features/profile/queries';
import { stanNotify } from '@/lib/stanNotify';
import { isAxiosError } from 'axios';
import { createIconElement } from '@/lib/utils/createIconElement';
import { useCollapse } from 'react-collapsed';
import IconChevronUp from '@/assets/icons/ic-chevron-up.svg';
import IconChevronRight from '@/assets/icons/ic-chevron-right.svg';
import { useOnboardingStepStatus } from '@/hooks/useOnboardingStepStatus';
import { useOnboardingCurrentStep } from '@/hooks/useOnboardingCurrentStep';
import { useOnboardingSessionStorage } from '@/hooks/useOnboardingSessionStorage';
import { useDirectDepositSetupBanner } from '@/hooks/useDirectDepositSetupBanner';
import { useIntegrationBanners } from '@/features/dashboard/hooks/useIntegrationBanners';

interface OnboardingChecklistSidebarProps {
    onAddProduct?: () => void;
}

export function OnboardingChecklistSidebar(
    { onAddProduct }: OnboardingChecklistSidebarProps = { onAddProduct: undefined }
) {
    const { user, refetchUser } = useUser();
    const navigate = useNavigate();
    const fileUploadRef = useRef<StanFileUploadRef>(null);
    const [pickedFile, setPickedFile] = useState<File | null>(null);
    const [isCropOpen, setIsCropOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { mutateAsync: updateProfileImageMutation } = useUpdateProfileImage();

    // Use hooks for onboarding logic
    const { isProfileImageUploaded, isSocialLinksAdded, isFirstProductAdded, isDirectDepositAdded } =
        useOnboardingStepStatus();

    const currentStep = useOnboardingCurrentStep();

    const { getAllStepsReady, markStepReady, refreshKey } = useOnboardingSessionStorage();

    // Check if any banners are showing (for positioning the desktop sidebar)
    const { showBanner: showWarningBanner } = useDirectDepositSetupBanner();
    const { hasStripeAccountIssues, hasPaypalAccountIssues, hasProductWithErroredIntegration, isErroredIntegration } =
        useIntegrationBanners();
    const hasBannerShowing =
        showWarningBanner ||
        hasStripeAccountIssues ||
        hasPaypalAccountIssues ||
        hasProductWithErroredIntegration ||
        isErroredIntegration('instagram');

    // Calculate completed tasks (including skipped steps)
    const completedTasks = useMemo(() => {
        const stepsReady = getAllStepsReady();

        let count = 0;
        if (isProfileImageUploaded || stepsReady.profileImage) count++;
        if (isSocialLinksAdded || stepsReady.socialLinks) count++;
        if (isDirectDepositAdded || stepsReady.depositSetup) count++;
        if (isFirstProductAdded || stepsReady.firstProduct) count++;
        return count;
    }, [
        isProfileImageUploaded,
        isSocialLinksAdded,
        isDirectDepositAdded,
        isFirstProductAdded,
        getAllStepsReady,
        refreshKey,
    ]);

    const openFilePicker = useCallback(() => {
        fileUploadRef.current?.onPickFile();
    }, []);

    const onFilePicked = useCallback((files: File[]) => {
        const file = files[0] || null;
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            stanNotify({
                type: 'error',
                title: 'Invalid File Type',
                text: 'Please select an image file.',
                duration: 3000,
            });
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            stanNotify({
                type: 'error',
                title: 'File Too Large',
                text: 'File size must be less than 5MB.',
                duration: 5000,
            });
            return;
        }
        setPickedFile(file);
        setIsCropOpen(true);
    }, []);

    const handleAvatarCropped = useCallback(
        async ({ croppedBlob }: { croppedBlob: Blob; croppedDataUrl: string }) => {
            try {
                setIsUploading(true);

                const file = new File(
                    [croppedBlob],
                    pickedFile?.name.replace(/\.[^.]+$/, '.png') || 'profile-image.png',
                    {
                        type: 'image/png',
                    }
                );

                const { url } = await uploadFile(file, { objectType: 'profile-image' });
                await updateProfileImageMutation(url);
                await refetchUser();

                // Mark step 1 as ready for next step
                markStepReady('profileImage');

                // Close modal and reset state only after everything is complete
                setIsCropOpen(false);
                setPickedFile(null);
                setIsUploading(false);

                stanNotify({
                    type: 'success',
                    title: 'Profile Updated',
                    text: 'Your profile picture has been updated successfully!',
                    duration: 3000,
                });
            } catch (error) {
                setIsUploading(false);
                let errorMessage = 'Failed to upload avatar. Please try again.';
                let errorTitle = 'Upload Failed';

                if (error instanceof Error && (error as Error & { isFileSizeError?: boolean }).isFileSizeError) {
                    errorTitle = 'File Size Error';
                    errorMessage = error.message;
                } else if (isAxiosError(error)) {
                    errorMessage = error.response?.data?.message || error.message;
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }

                stanNotify({
                    type: 'error',
                    title: errorTitle,
                    text: errorMessage,
                    duration: 5000,
                });
            }
        },
        [pickedFile, updateProfileImageMutation, refetchUser, markStepReady]
    );

    const handleProfileImage = () => {
        openFilePicker();
    };

    const handleSocialLinks = () => {
        navigate('/profile?onboarding=social-links');
    };

    const handleDirectDeposit = () => {
        navigate('/account_settings?tab=store');
    };

    const handleFirstProduct = () => {
        if (onAddProduct) {
            // Use the handler from parent if provided (for DynamicDashboardV8)
            onAddProduct();
        } else {
            // Fallback to direct navigation (for other contexts)
            const storeId = user?.default_store_id;
            if (storeId) {
                navigate(`/stores/${storeId}/page/create`);
            }
        }
    };

    const toggleCollapse = () => {
        setIsCollapsed(prev => !prev);
    };

    const { getCollapseProps } = useCollapse({
        isExpanded: !isCollapsed,
        duration: 300,
        easing: 'ease-out',
    });

    // Get all steps for the compact list (show all, mark completed ones)
    const allSteps = useMemo(() => {
        const stepsReady = getAllStepsReady();

        return [
            {
                number: 1,
                title: 'Add profile photo',
                isCompleted: isProfileImageUploaded || stepsReady.profileImage,
                isActive: currentStep === 1,
                onClick: handleProfileImage,
            },
            {
                number: 2,
                title: 'Connect socials',
                isCompleted: isSocialLinksAdded || stepsReady.socialLinks,
                isActive: currentStep === 2,
                onClick: handleSocialLinks,
            },
            {
                number: 3,
                title: 'Set up direct deposit',
                isCompleted: isDirectDepositAdded || stepsReady.depositSetup,
                isActive: currentStep === 3,
                onClick: handleDirectDeposit,
            },
            {
                number: 4,
                title: 'Create first product',
                isCompleted: isFirstProductAdded || stepsReady.firstProduct,
                isActive: currentStep === 4,
                onClick: handleFirstProduct,
            },
        ];
    }, [
        isProfileImageUploaded,
        isSocialLinksAdded,
        isDirectDepositAdded,
        isFirstProductAdded,
        currentStep,
        getAllStepsReady,
    ]);

    const totalSteps = 4;
    const progressPercentage = (completedTasks / totalSteps) * 100;

    return (
        <>
            {/* Mobile version - appears above dashboard content */}
            <div className='bg-neutral-white border-neutral-subtle shadow-stan mb-6 w-full rounded-lg border lg:hidden'>
                {/* Header */}
                <div className='border-neutral-subtle flex items-center justify-between border-b p-4'>
                    <h3 className='text-primary-main text-base font-bold'>Store setup</h3>
                    <div className='flex items-center gap-2'>
                        <span className='text-gray text-sm'>
                            {completedTasks}/{totalSteps}
                        </span>
                        <button
                            onClick={toggleCollapse}
                            className='text-gray hover:text-primary-main cursor-pointer transition-colors'
                            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                        >
                            {createIconElement(isCollapsed ? IconChevronRight : IconChevronUp, {
                                className: 'size-4 transition-transform duration-300',
                            })}
                        </button>
                    </div>
                </div>

                {/* Collapsible content */}
                <div {...getCollapseProps()}>
                    {/* Progress bar */}
                    <div className='bg-neutral-soft h-1.5 w-full'>
                        <div
                            className='bg-brand-subtle h-full transition-all duration-300'
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>

                    {/* Checklist items */}
                    <div className='flex flex-col p-4'>
                        {allSteps.map(step => (
                            <button
                                key={step.number}
                                onClick={step.onClick}
                                className='flex cursor-pointer items-center gap-3 rounded-md p-2 text-left transition-colors'
                            >
                                {/* Status indicator */}
                                <div
                                    className={`flex size-6 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                                        step.isCompleted
                                            ? 'bg-brand-subtle border-brand-subtle'
                                            : step.isActive
                                              ? 'border-brand-main bg-canvas'
                                              : 'border-neutral-subtle bg-canvas'
                                    }`}
                                >
                                    {step.isCompleted && <span className='text-brand-main text-xs font-bold'>✓</span>}
                                </div>

                                {/* Step title */}
                                <span className='text-primary-main flex-1 text-sm'>{step.title}</span>

                                {/* Chevron icon */}
                                {createIconElement(IconChevronRight, {
                                    className: 'text-gray size-4 flex-shrink-0',
                                })}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Desktop version - fixed sidebar on the right */}
            <div
                className={`bg-neutral-white border-neutral-subtle shadow-stan z-30 hidden w-80 rounded-lg border lg:block ${hasBannerShowing ? 'top-20' : ''}`}
            >
                {/* Header */}
                <div className='border-neutral-subtle flex items-center justify-between border-b p-4'>
                    <h3 className='text-primary-main text-base font-bold'>Store setup</h3>
                    <div className='flex items-center gap-2'>
                        <span className='text-gray text-sm'>
                            {completedTasks}/{totalSteps}
                        </span>
                        <button
                            onClick={toggleCollapse}
                            className='text-gray hover:text-primary-main cursor-pointer transition-colors'
                            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                        >
                            {createIconElement(isCollapsed ? IconChevronRight : IconChevronUp, {
                                className: 'size-4 transition-transform duration-300',
                            })}
                        </button>
                    </div>
                </div>

                {/* Collapsible content */}
                <div {...getCollapseProps()}>
                    {/* Progress bar */}
                    <div className='bg-neutral-soft h-1.5 w-full'>
                        <div
                            className='bg-brand-subtle h-full transition-all duration-300'
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>

                    {/* Checklist items */}
                    <div className='flex flex-col p-4'>
                        {allSteps.map(step => (
                            <button
                                key={step.number}
                                onClick={step.onClick}
                                className='flex cursor-pointer items-center gap-3 rounded-md p-2 text-left transition-colors'
                            >
                                {/* Status indicator */}
                                <div
                                    className={`flex size-6 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                                        step.isCompleted
                                            ? 'bg-brand-subtle border-brand-subtle'
                                            : step.isActive
                                              ? 'border-brand-main bg-canvas'
                                              : 'border-neutral-subtle bg-canvas'
                                    }`}
                                >
                                    {step.isCompleted && <span className='text-brand-main text-xs font-bold'>✓</span>}
                                </div>

                                {/* Step title */}
                                <span className='text-primary-main flex-1 text-sm'>{step.title}</span>

                                {/* Chevron icon */}
                                {createIconElement(IconChevronRight, {
                                    className: 'text-gray size-4 flex-shrink-0',
                                })}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* File upload component */}
            <StanFileUpload
                ref={fileUploadRef}
                accept='image/png,image/jpeg,image/jpg,image/webp'
                confirmUpload={false}
                onFilePicked={onFilePicked}
            />

            {/* Crop modal */}
            <StanCrop
                isOpen={isCropOpen}
                imageFile={pickedFile}
                aspectRatio={1}
                width={600}
                height={600}
                borderRadius='circle'
                isLoading={isUploading}
                onClose={() => {
                    // Prevent closing during upload
                    if (!isUploading) {
                        setIsCropOpen(false);
                        setPickedFile(null);
                    }
                }}
                onConfirm={({ croppedBlob, croppedDataUrl }) => handleAvatarCropped({ croppedBlob, croppedDataUrl })}
            />
        </>
    );
}
